<template>
  <div>
    <HMModalHeader
      :title="$t('PAYMENT_PLANS.TITLE.INVOICES')"
      @close="$emit('close')"
    />
    <form>
      <EventStudentComponent
        v-if="paymentPlan.student"
        :student="paymentPlan.student"
      >
        <HMTag :status="paymentPlan.status">{{ paymentPlanStatus }}</HMTag>
      </EventStudentComponent>
      <div class="row mt-4">
        <div class="col-4">
          <h4>
            {{ $formatCurrency(getTotalPaid, paymentPlan.currency) }}
          </h4>
          <span class="smaller">
            {{ $t('PAYMENT_PLANS.LABELS.TOTAL_PAID') }}
          </span>
        </div>
        <div class="col-4">
          <h4>
            {{ $formatCurrency(getAmountPaymentsLeft, paymentPlan.currency) }}
          </h4>
          <span class="smaller">
            {{ $t('PAYMENT_PLANS.LABELS.LEFT_TO_PAY') }}
          </span>
        </div>
        <div class="col-4">
          <h4>
            {{ getPaymentsLeft }}
          </h4>
          <span class="smaller">
            {{ $t('PAYMENT_PLANS.LABELS.PAYMENTS_LEFT') }}
          </span>
        </div>
        <div class="col-12">
          <hr />
        </div>
      </div>
      <div class="row">
        <div class="col-4">
          <h4>
            {{ formatDate(paymentPlan.created) }}
          </h4>
          <span class="smaller">
            {{ $t('PAYMENT_PLANS.LABELS.START_DATE') }}
          </span>
        </div>
        <div class="col-4">
          <h4>
            {{ formatDate(getEndPaymentDate) }}
          </h4>
          <span class="smaller">
            {{ $t('PAYMENT_PLANS.LABELS.END_DATE') }}
          </span>
        </div>
        <div class="col-4">
          <h4>
            {{
              getPaymentsLeft ? formatDate(paymentPlan.next_billing_date) : '/'
            }}
          </h4>
          <span class="smaller">
            {{ $t('PAYMENT_PLANS.LABELS.NEXT_PAYMENT') }}
          </span>
        </div>
        <div class="col-12">
          <hr />
        </div>
      </div>
      <HMTable class="mt-3" :items="invoices" :fields="invoiceFelds">
        <template slot="created" slot-scope="{ data }">
          <h5>{{ formatDate(data.created) }}</h5>
        </template>
        <template slot="amount" slot-scope="{ data }">
          <span>{{ $formatCurrency(data.amount, paymentPlan.currency) }}</span>
        </template>
        <template slot="status" slot-scope="{ data }">
          <HMTag
            :class="{
              'bg-coral': data.status === 'failed',
              'c-white': data.status === 'failed',
            }"
          >
            {{ getStatusText(data.status) }}
          </HMTag>
        </template>
      </HMTable>
      <div class="form-group hm-modal-footer mt-3" v-if="student">
        <div class="submit-group">
          <HMButton color="outline-gray" @click="$emit('close')">
            {{ $t('GENERAL.BUTTON.GO_BACK') }}
          </HMButton>
          <HMButton
            color="brand"
            :disabled="cancelling"
            :loading="cancelling"
            @click="updateStudentCreditCard"
          >
            {{ $t('PAYMENT_PLANS.BUTTON.UPDATE_CREDIT_CARD') }}
          </HMButton>
        </div>
      </div>
      <div class="form-group hm-modal-footer mt-3" v-else>
        <div class="submit-group">
          <HMButton color="outline-gray" @click="$emit('close')">
            {{ $t('GENERAL.BUTTON.GO_BACK') }}
          </HMButton>
          <div class="d-flex">
            <!--            <HMButton-->
            <!--                class="mr-2"-->
            <!--                color="outline-gray"-->
            <!--                :disabled="cancelling"-->
            <!--                :loading="cancelling"-->
            <!--                @click="updateStudentCreditCardAsTeacher"-->
            <!--            >-->
            <!--              {{ $t('PAYMENT_PLANS.BUTTON.UPDATE_CREDIT_CARD') }}-->
            <!--            </HMButton>-->
            <HMButton
              v-if="canCancelPaymentPlan"
              color="brand"
              :disabled="cancelling"
              :loading="cancelling"
              @click="cancelPaymentPlan"
            >
              {{ $t('PAYMENT_PLANS.BUTTON.CANCEL_PAYMENT_PLAN') }}
            </HMButton>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import moment from 'moment';
import { errorToMessage } from '@/core/utils/errors';
import { formatDate } from '@/web/utils/format';
import EventStudentComponent from '@/web/components/events/EventStudentComponent';
import HMDialog from '@/web/components/common/HMDialog';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import StudentChangePaymentCardModal from '@/web/components/settings/StudentChangePaymentCardModal.vue';

export default {
  name: 'PaymentPlanInvoicesModal',
  components: {
    EventStudentComponent,
  },
  props: {
    paymentPlan: {
      type: Object,
    },
    refreshFn: null,
    student: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    getPaidInvoices() {
      return this.invoices.filter(i => i.status === 'succeeded');
    },
    getTotalPayments() {
      return this.paymentPlan.number_of_subsequent_payments + 1;
    },
    getTotalPaid() {
      return this.getPaidInvoices.reduce(
        (accumulator, i) => accumulator + parseFloat(i.amount),
        0
      );
    },
    getPaymentsLeft() {
      const numberOfPaidInvoices = this.getPaidInvoices.length;
      const totalPayments = this.getTotalPayments;
      return totalPayments - numberOfPaidInvoices;
    },
    getAmountPaymentsLeft() {
      return (
        this.getPaymentsLeft *
        parseFloat(this.paymentPlan.subsequent_payment_amount)
      ).toFixed(2);
    },
    getEndPaymentDate() {
      const started = moment(this.paymentPlan.created);
      const endPaymenDate = started.add(
        this.paymentPlan.number_of_subsequent_payments *
          this.paymentPlan.billing_frequency_days,
        'days'
      );
      return endPaymenDate.toDate();
    },
    invoiceFelds() {
      return [
        {
          name: 'created',
          title: this.$t('PAYMENT_PLANS.TABLE.DATE'),
        },
        {
          name: 'amount',
          title: this.$t('PAYMENT_PLANS.TABLE.AMOUNT'),
        },
        {
          name: 'status',
          title: this.$t('PAYMENT_PLANS.TABLE.STATUS'),
        },
      ];
    },
    paymentPlanStatus() {
      return this.$t(
        `PAYMENT_PLANS.STATUS.${this.paymentPlan.status.toUpperCase()}`
      );
    },
    canCancelPaymentPlan() {
      if (this.paymentPlan.status === 'cancelled') return false;

      return this.getPaymentsLeft > 0;
    },
    studioURL() {
      return getStudioURL(this.$route);
    },
  },
  data() {
    return {
      invoices: [],
      cancelling: false,
    };
  },
  methods: {
    updateStudentCreditCard() {
      const studioURL = getStudioURL(this.$route);

      this.$modal.show(StudentChangePaymentCardModal, {
        studioURL: studioURL,
        productId: this.paymentPlan.product.id,
        cardChangedFn: async payload => {
          const data = {
            studioURL: studioURL,
            id: this.paymentPlan.id,
            defaultPaymentMethod: payload.paymentMethod,
          };
          await this.$store.dispatch(
            'studio/updateStudentPaymentPlanPaymentMethod',
            data
          );
        },
      });
    },
    updateStudentCreditCardAsTeacher() {
      const studioURL = this.$store.getters['user/teacherStudioUrl'];

      // TODO this needs refactor, because setupintent is retrieved for a student from request
      this.$modal.show(StudentChangePaymentCardModal, {
        studioURL: studioURL,
        productId: this.paymentPlan.product.id,
        cardChangedFn: async payload => {
          const data = {
            id: this.paymentPlan.id,
            defaultPaymentMethod: payload.paymentMethod,
          };
          await this.$store.dispatch(
            'sale/updateStudentPaymentPlanPaymentMethodAsTeacher',
            data
          );
        },
      });
    },
    cancelPaymentPlan() {
      const title = this.$t('PAYMENT_PLANS.TITLE.CANCEL_PAYMENT_PLAN');
      const text = this.$t('PAYMENT_PLANS.TEXT.CANCEL_PAYMENT_PLAN');
      const confirmButton = this.$t('PAYMENT_PLANS.BUTTON.CANCEL_PAYMENT_PLAN');
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      const confirm = async () => {
        try {
          this.cancelling = true;
          await this.$store.dispatch(
            'sale/cancelPaymentPlan',
            this.paymentPlan.id
          );
          this.$notify({
            group: 'app',
            type: 'success',
            title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
            text: this.$t(
              'NOTIFICATIONS.TEXT.PAYMENT_PLAN_SUCCESSFULLY_CANCELLED'
            ),
          });
        } catch (error) {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Failed to cancel this payment plan',
            text: errorToMessage(error.response, `Request failed: ${error}`),
            duration: -1, // stay ON
          });
        } finally {
          this.cancelling = false;
        }
        if (this.refreshFn) this.refreshFn();
        this.$emit('close');
      };

      this.$modal.show(HMDialog, {
        title,
        text,
        confirmButton,
        confirm,
      });
    },
    formatDate(date) {
      return formatDate(date, 'date-word');
    },
    getStatusText(status) {
      if (status === 'succeeded') {
        return this.$t('PAYMENT_PLANS.LABELS.PAID');
      } else {
        return this.$t('PAYMENT_PLANS.LABELS.FAILED');
      }
    },
    async loadData() {
      if (this.student) {
        this.invoices = await this.$store.dispatch(
          'studio/getPaymentPlanInvoices',
          { studioURL: this.studioURL, paymentPlanId: this.paymentPlan.id }
        );
      } else {
        this.invoices = await this.$store.dispatch(
          'sale/getPaymentPlanInvoices',
          this.paymentPlan.id
        );
      }
    },
  },
  async mounted() {
    try {
      this.loadData();
    } catch (error) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: 'Failed to load payment plan statistics',
        text: errorToMessage(error?.response, `Request failed: ${error}`),
        duration: -1, // stay ON
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

::v-deep .tag {
  &--active {
    color: $forest;
    background-color: lighten($forest, 60%) !important;
  }
  &--completed {
    color: $dark-gray;
  }
  &--past_due {
    color: $mustard;
    background-color: lighten($mustard, 50%) !important;
  }
  &--cancelled {
    color: $coral;
    background-color: lighten($coral, 25%) !important;
  }
}
.submit-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.form-group {
  padding-top: 1rem;
  margin: 0;
}
</style>
